'use client';

import Script from 'next/script';
import { useConsent } from '@/lib/consent-client';

/**
 * Carga gtag.js (Google Analytics 4) solo cuando:
 *   1. `gaId` está definido (env var NEXT_PUBLIC_GA4_ID)
 *   2. El consent.analytics del usuario es `true`
 *
 * Implementa Google Consent Mode v2: si el user rechaza, NUNCA se carga el
 * script y `gtag` queda undefined → todos nuestros wrappers `trackEvent` hacen
 * noop. Si el user después acepta, el componente re-renderiza (vía
 * `useConsent`), Next.js inyecta el `<Script>`, gtag se inicializa y los
 * eventos posteriores fluyen normalmente.
 *
 * No hace falta gating adicional con feature flag — si la env var no está,
 * el script tampoco se carga (returns null).
 */
export function GoogleAnalytics({ gaId }: { gaId: string | undefined }) {
  const { has } = useConsent();
  if (!gaId) return null;
  if (!has('analytics')) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
        strategy="afterInteractive"
      />
      <Script id="ga4-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'granted',
            functionality_storage: 'granted',
            personalization_storage: 'granted',
            security_storage: 'granted',
            wait_for_update: 500,
          });
          gtag('config', '${gaId}', {
            anonymize_ip: true,
            send_page_view: true,
          });
        `}
      </Script>
    </>
  );
}
